import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { ProgressBar } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, CheckCircle2, Circle, Lock, Clock, Paperclip, Play,
  PanelLeftClose, PanelLeft, ClipboardCheck, Award, Headphones, FileText, Download,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AssessmentPanel from "@/components/AssessmentPanel";

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const [showAssessment, setShowAssessment] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/courses/${courseId}`);
    setData(data);
    setActiveId((prev) => {
      if (prev) return prev;
      const firstIncomplete = data.lessons.find((l) => l.unlocked && !l.completed);
      return (firstIncomplete || data.lessons[0])?.id;
    });
  }, [courseId]);

  const bounced = useRef(false);
  useEffect(() => {
    load().catch((err) => {
      if (bounced.current) return;
      bounced.current = true;
      const status = err.response?.status;
      toast.error(status === 403 ? "You don't have access to this course." : "Course not found.");
      navigate("/classroom");
    });
  }, [load, navigate]);

  if (!data) return <div className="py-20 text-center text-muted-foreground">Loading course…</div>;

  const { course, modules, lessons, assessment } = data;
  const active = lessons.find((l) => l.id === activeId);
  const lessonsDone = course.lesson_count > 0 && course.completed_lessons >= course.lesson_count;

  const toggleComplete = async () => {
    if (!active) return;
    const endpoint = active.completed ? "incomplete" : "complete";
    try {
      await api.post(`/lessons/${active.id}/${endpoint}`);
      if (endpoint === "complete") toast.success("Lesson complete");
      await load();
    } catch (e) {
      toast.error("Could not update progress");
    }
  };

  const idx = lessons.findIndex((l) => l.id === activeId);
  const next = lessons[idx + 1];

  return (
    <div className="fade-in">
      <button
        onClick={() => navigate("/classroom")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="course-back-button"
      >
        <ChevronLeft className="h-4 w-4" /> Back to Classroom
      </button>

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-[24px] font-semibold sm:text-[28px]">{course.title}</h1>
          <div className="mt-1.5 flex items-center gap-3 text-sm text-muted-foreground">
            <span className="tnum">{course.cpd_hours} CPD hours</span>
            <span>·</span>
            <span className="tnum">{course.completed_lessons}/{course.lesson_count} lessons</span>
          </div>
        </div>
        <div className="w-full max-w-xs">
          <div className="mb-1.5 flex justify-between text-[13px]">
            <span className="text-muted-foreground">Progress</span>
            <span className="tnum font-semibold text-[#1779AC] dark:text-[#5cc7ef]">{course.progress_pct}%</span>
          </div>
          <ProgressBar value={course.progress_pct} />
        </div>
      </div>

      <div className={cn("grid gap-6", listOpen ? "lg:grid-cols-[320px_1fr]" : "lg:grid-cols-[0_1fr]")}>
        {/* Lesson list */}
        <aside className={cn("space-y-4", !listOpen && "hidden lg:block lg:w-0 lg:overflow-hidden")}>
          {modules.map((m) => (
            <div key={m.id} className="card-surface overflow-hidden">
              <div className="border-b border-border bg-secondary/50 px-4 py-2.5 text-[13px] font-semibold">{m.title}</div>
              <div className="divide-y divide-border">
                {m.lessons.map((l) => {
                  const isActive = l.id === activeId;
                  const locked = !l.unlocked;
                  return (
                    <button
                      key={l.id}
                      disabled={locked}
                      onClick={() => { setActiveId(l.id); setShowAssessment(false); }}
                      data-testid={`lesson-item-${l.id}`}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
                        isActive ? "bg-[#33B6E4]/10" : "hover:bg-accent",
                        locked && "cursor-not-allowed opacity-55"
                      )}
                    >
                      {locked ? (
                        <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : l.completed ? (
                        <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-[#33B6E4]" />
                      ) : (
                        <Circle className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                      )}
                      <span className={cn("flex-1 leading-snug", isActive && "font-medium")}>{l.title}</span>
                      <span className="tnum shrink-0 text-[11px] text-muted-foreground">{l.cpd_minutes}m</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {assessment && (
            <button
              onClick={() => setShowAssessment(true)}
              data-testid="open-assessment-nav"
              className={cn("card-surface flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
                showAssessment ? "ring-2 ring-[#33B6E4]" : "hover:bg-accent")}
            >
              {course.assessment_passed ? (
                <Award className="h-[18px] w-[18px] shrink-0 text-emerald-500" />
              ) : lessonsDone ? (
                <ClipboardCheck className="h-[18px] w-[18px] shrink-0 text-[#33B6E4]" />
              ) : (
                <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="flex-1 font-medium leading-snug">Final assessment</span>
              <span className="tnum shrink-0 text-[11px] text-muted-foreground">{assessment.question_count}Q</span>
            </button>
          )}
        </aside>

        {/* Lesson stage */}
        <div>
          {showAssessment ? (
            <AssessmentPanel courseId={courseId} onPassed={load} />
          ) : active ? (
            <div className="card-surface overflow-hidden">
              {active.video_url ? (
                <div className="relative aspect-video w-full bg-black">
                  {active.video_provider === "youtube" || active.video_provider === "vimeo" ? (
                    <iframe
                      key={active.id}
                      src={active.video_url}
                      title={active.title}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="lesson-video-frame"
                    />
                  ) : (
                    <video key={active.id} controls playsInline preload="metadata" className="h-full w-full" data-testid="lesson-video-player"
                      src={`${BACKEND_URL}${active.video_url}`} crossOrigin="use-credentials" />
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 border-b border-border bg-[#0D0F12] px-6 py-5 text-white sm:px-8">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#33B6E4] text-[#0D0F12]"><Headphones className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">Listen to this lesson</div>
                    <div className="text-xs text-white/60">AI narration of the full lesson text. Video version coming soon.</div>
                    <audio key={active.id} controls preload="none" className="mt-2 w-full" data-testid="lesson-audio-player"
                      src={`${BACKEND_URL}/api/lessons/${active.id}/audio.mp3`} crossOrigin="use-credentials" />
                  </div>
                </div>
              )}

              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={() => setListOpen((v) => !v)}
                    className="hidden items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground lg:inline-flex"
                    data-testid="toggle-lesson-list"
                  >
                    {listOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
                    {listOpen ? "Hide lessons" : "Show lessons"}
                  </button>
                  <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> <span className="tnum">{active.cpd_minutes}</span> CPD minutes
                  </span>
                </div>

                <h2 className="mt-3 font-heading text-[22px] font-semibold">{active.title}</h2>

                <div className="lesson-prose mt-4 text-foreground/90" dangerouslySetInnerHTML={{ __html: active.content }} />

                <div className="mt-6">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Resources</div>
                  <div className="space-y-2">
                    <a
                      href={`${BACKEND_URL}/api/lessons/${active.id}/summary.pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid="lesson-summary-pdf"
                      className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-2.5 text-sm hover:bg-accent"
                    >
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1">One page summary (PDF)</span>
                      <Download className="h-4 w-4 text-muted-foreground" />
                    </a>
                    {(active.attachments || []).map((a, i) => (
                      <a
                        key={i}
                        href={a.url}
                        target="_blank" rel="noreferrer"
                        data-testid={`lesson-attachment-${i}`}
                        className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-2.5 text-sm hover:bg-accent"
                      >
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1">{a.name}</span>
                        <span className="text-xs text-muted-foreground">{a.size}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {active.has_quiz && (
                  <div className="mt-6 flex items-center gap-3 rounded-lg border border-dashed border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
                    <ClipboardCheck className="h-5 w-5 text-[#33B6E4]" />
                    End of lesson quiz. Interactive quizzes arrive in Phase 2.
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border pt-6">
                  <Button
                    onClick={toggleComplete}
                    data-testid="lesson-complete-toggle"
                    className={cn("h-11", active.completed ? "" : "btn-brand")}
                    variant={active.completed ? "outline" : "default"}
                  >
                    {active.completed ? (
                      <><CheckCircle2 className="mr-2 h-4 w-4" /> Completed. Mark as not done</>
                    ) : (
                      <><CheckCircle2 className="mr-2 h-4 w-4" /> Mark lesson complete</>
                    )}
                  </Button>
                  {next && (
                    <Button
                      variant="outline"
                      className="h-11"
                      data-testid="lesson-next-button"
                      disabled={!next.unlocked}
                      onClick={() => setActiveId(next.id)}
                    >
                      <Play className="mr-2 h-4 w-4" /> Next lesson
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card-surface p-10 text-center text-muted-foreground">Select a lesson to begin.</div>
          )}
        </div>
      </div>
    </div>
  );
}
